// File: frontend/src/pages/PayrollPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, payrollAPI } from '../services/api';
import toast from 'react-hot-toast';
import Spinner from '../components/Spinner';
import { useAuth } from '../AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const formatMonthLabel = (monthStr) => {
    const [y, m] = monthStr.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

// Monthly income history + trend chart. Admin/accountant can filter by a
// specific teacher or view the combined total across all teachers; a
// teacher viewing this only ever sees their own numbers (enforced
// server-side too, not just hidden in the UI).
function MonthlyIncomeTrend({ teachers, isPrivileged }) {
    const [teacherFilter, setTeacherFilter] = useState('');
    const [series, setSeries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchTrend = useCallback(async () => {
        setIsLoading(true);
        try {
            const params = { months: 12 };
            if (isPrivileged && teacherFilter) params.teacherId = teacherFilter;
            const data = await payrollAPI.getMonthlyTrend(params);
            setSeries(data.series || []);
        } catch (err) {
            toast.error('Could not load monthly income history.');
        } finally {
            setIsLoading(false);
        }
    }, [teacherFilter, isPrivileged]);

    useEffect(() => { fetchTrend(); }, [fetchTrend]);

    const chartData = series.map(s => ({ ...s, label: formatMonthLabel(s.month) }));
    const totalOverRange = series.reduce((sum, s) => sum + (s.income || 0), 0);
    const currentMonthIncome = series.length > 0 ? series[series.length - 1].income : 0;

    return (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-5 rounded-2xl shadow-xs border border-gray-200 dark:border-gray-700 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <span>📈</span> {isPrivileged ? 'Monthly Income Overview' : 'My Monthly Income'}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Last 12 months, based on recorded attendance + approved expenses</p>
                </div>
                {isPrivileged && (
                    <select
                        value={teacherFilter}
                        onChange={e => setTeacherFilter(e.target.value)}
                        className="border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800"
                    >
                        <option value="">All Teachers (Combined)</option>
                        {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                    </select>
                )}
            </div>

            {isLoading ? (
                <div className="py-10"><Spinner /></div>
            ) : (
                <>
                    <div className="grid grid-cols-2 gap-2.5">
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold block">This Month</span>
                            <span className="text-base font-black text-emerald-700">NT${Math.round(currentMonthIncome).toLocaleString()}</span>
                        </div>
                        <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold block">Last 12 Months Total</span>
                            <span className="text-base font-black text-gray-900 dark:text-gray-100">NT${Math.round(totalOverRange).toLocaleString()}</span>
                        </div>
                    </div>

                    {chartData.every(c => c.income === 0) ? (
                        <p className="text-gray-400 dark:text-gray-500 italic text-center py-10 text-sm">No income recorded in this period yet.</p>
                    ) : (
                        <div style={{ width: '100%', height: 260 }}>
                            <ResponsiveContainer>
                                <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
                                    <Tooltip formatter={(value) => [`NT$${Math.round(value).toLocaleString()}`, 'Income']} />
                                    <Line type="monotone" dataKey="income" stroke="#059669" strokeWidth={2.5} name="Income" dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Simple month-by-month history table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-xs">
                            <thead>
                                <tr className="text-gray-500 dark:text-gray-400 uppercase text-[10px]">
                                    <th className="text-left py-1.5 px-2">Month</th>
                                    <th className="text-right py-1.5 px-2">Income</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {[...series].reverse().map(s => (
                                    <tr key={s.month}>
                                        <td className="py-1.5 px-2 text-gray-700 dark:text-gray-300">{formatMonthLabel(s.month)}</td>
                                        <td className="py-1.5 px-2 text-right font-semibold text-gray-900 dark:text-gray-100">NT${Math.round(s.income).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

// Groups cram-school class rows by cram_school_name so a teacher who works
// at multiple cram schools gets a per-school total instead of one lump sum
// (mirrors groupCramBySchool in backend/routes/payroll.js).
const groupCramBySchool = (cramClasses) => {
    const map = new Map();
    cramClasses.forEach(c => {
        const key = (c.cram_school_name && c.cram_school_name.trim()) || 'Unspecified Cram School';
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(c);
    });
    return Array.from(map.entries())
        .map(([schoolName, sessions]) => ({
            schoolName,
            sessions,
            hours: sessions.reduce((sum, c) => sum + parseFloat(c.class_duration_hours || 0), 0),
            pay: sessions.reduce((sum, c) => sum + parseFloat(c.session_pay || 0), 0)
        }))
        .sort((a, b) => a.schoolName.localeCompare(b.schoolName));
};

// Trigger a file download from a blob
const downloadFile = (blob, filename) => {
    const url = window.URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
};

export default function PayrollPage() {
    const { currentUser } = useAuth();
    const isPrivileged = currentUser?.role === 'admin' || currentUser?.role === 'accountant';
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedSchools, setExpandedSchools] = useState(new Set());

    const toggleSchoolExpanded = (schoolName) => {
        setExpandedSchools(prev => {
            const next = new Set(prev);
            if (next.has(schoolName)) next.delete(schoolName);
            else next.add(schoolName);
            return next;
        });
    };

    useEffect(() => {
        if (!isPrivileged) return;
        // Fetch users with the 'teacher' or 'admin' role (e.g. Sule who teaches classes)
        usersAPI.getAll().then(allUsers => {
            setTeachers(allUsers.filter(u => u.role === 'teacher' || u.role === 'admin'));
        });
    }, [isPrivileged]);

    const handleGenerate = async () => {
        if (!selectedTeacher || !fromDate || !toDate) {
            toast.error("Please select a teacher and a date range.");
            return;
        }
        setIsLoading(true);
        setReport(null);
        setExpandedSchools(new Set());
        try {
            const data = await payrollAPI.generate({ teacherId: selectedTeacher, fromDate, toDate });
            setReport(data);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to generate report.");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePrintPayslip = async () => {
        const params = { teacherId: selectedTeacher, fromDate, toDate };
        if (expandedSchools.size > 0) {
            params.expandedSchools = Array.from(expandedSchools).join(',');
        }
        const teacherName = teachers.find(t => String(t.id) === String(selectedTeacher))?.username || 'teacher';
        const filename = `payslip_${teacherName}_${fromDate}_to_${toDate}.pdf`;

        const toastId = toast.loading('Generating compressed 1-page PDF...');
        try {
            const pdfBlob = await payrollAPI.generatePayslip(params);
            downloadFile(pdfBlob, filename);
            toast.success('Payslip downloaded!', { id: toastId });
        } catch (err) {
            toast.error('Could not generate payslip.', { id: toastId });
        }
    };

    const handleBrowserPrint = () => {
        window.print();
    };

    // Calculate separated divisions from report
    const tutoringClasses = report?.tutoringClasses || (report?.classes || []).filter(c => 
        c.class_type !== 'cram_school' && (!c.cram_school_name || c.cram_school_name.trim() === '')
    );
    const cramClasses = report?.cramClasses || (report?.classes || []).filter(c => 
        c.class_type === 'cram_school' || (c.cram_school_name && c.cram_school_name.trim() !== '')
    );
    const expenses = report?.expenses || [];

    const tutoringHours = tutoringClasses.reduce((sum, c) => sum + parseFloat(c.class_duration_hours || 0), 0);
    const tutoringPay = tutoringClasses.reduce((sum, c) => sum + parseFloat(c.session_pay || 0), 0);

    const cramHours = cramClasses.reduce((sum, c) => sum + parseFloat(c.class_duration_hours || 0), 0);
    const cramPay = cramClasses.reduce((sum, c) => sum + parseFloat(c.session_pay || 0), 0);
    const cramBySchool = groupCramBySchool(cramClasses);

    const expensesPay = expenses.reduce((sum, exp) => sum + parseFloat(exp.amount || 0), 0);
    const grandTotal = tutoringPay + cramPay + expensesPay;
    const currentTeacherName = teachers.find(t => t.id === parseInt(selectedTeacher))?.username || 'Teacher';

    return (
        <div className="space-y-4">
            {/* Print CSS Styles to guarantee compact 1-page print */}
            <style dangerouslySetInnerHTML={{ __html: `
                @media print {
                    nav, header, .no-print, #admin-panel, footer { display: none !important; }
                    body { background: white !important; font-size: 11px !important; margin: 0 !important; padding: 0 !important; }
                    .printable-payslip { border: none !important; box-shadow: none !important; padding: 0 !important; width: 100% !important; max-width: 100% !important; }
                    .page-break-avoid { break-inside: avoid; }
                    @page { size: A4 portrait; margin: 8mm; }
                }
            `}} />

            {/* Header Toolbar */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 no-print">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <span>💰</span> {isPrivileged ? 'Teacher Payroll & Payslip' : 'My Income'}
                </h2>
                {isPrivileged && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                        Dual-division monthly statements (Tutoring vs. Cram Schools)
                    </span>
                )}
            </div>

            <div className="no-print">
                <MonthlyIncomeTrend teachers={teachers} isPrivileged={isPrivileged} />
            </div>

            {!isPrivileged ? null : (
            <>
            {/* Control Panel (Hidden on Print) */}
            <div className="p-4 sm:p-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl shadow-xs grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 items-end no-print">
                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">Teacher</label>
                    <select value={selectedTeacher} onChange={e => setSelectedTeacher(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2.5 text-sm bg-white dark:bg-gray-800">
                        <option value="">-- Select Teacher --</option>
                        {teachers.map(t => <option key={t.id} value={t.id}>{t.username}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">From Date</label>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1">To Date</label>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full border border-gray-300 dark:border-gray-600 rounded-lg p-2 text-sm bg-white dark:bg-gray-800" />
                </div>
                <button onClick={handleGenerate} disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2.5 px-4 rounded-lg font-bold text-sm shadow-xs transition-colors disabled:bg-gray-400 cursor-pointer">
                    {isLoading ? 'Generating...' : 'Generate Report'}
                </button>
            </div>

            {isLoading && <div className="py-8"><Spinner /></div>}

            {/* Compressed & Divided Payslip Report */}
            {report && (
                <div className="printable-payslip bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-xs border border-gray-200 dark:border-gray-700 space-y-4">
                    
                    {/* Payslip Header & Action Buttons */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-gray-200 dark:border-gray-700 pb-3.5">
                        <div>
                            <div className="flex items-center gap-2">
                                <img src="/logo192.png" alt="Irie Roots" className="w-6 h-6 rounded-md object-cover inline-block" />
                                <h3 className="text-lg sm:text-xl font-black text-gray-900 dark:text-gray-100 tracking-tight">
                                    Teacher Payslip: <span className="text-emerald-700">{currentTeacherName}</span>
                                </h3>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Statement Period: <span className="font-semibold text-gray-700 dark:text-gray-300">{fromDate}</span> ~ <span className="font-semibold text-gray-700 dark:text-gray-300">{toDate}</span>
                                <span className="hidden sm:inline"> • Issued: {new Date().toLocaleDateString('zh-TW')}</span>
                            </p>
                        </div>
                        
                        <div className="flex items-center gap-2 w-full sm:w-auto no-print flex-wrap">
                            <button 
                                onClick={handleBrowserPrint} 
                                className="flex-1 sm:flex-initial bg-white dark:bg-gray-800 hover:bg-gray-50 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 px-3 py-1.5 rounded-lg font-bold text-xs shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                                title="Print or save as 1-page PDF using your browser"
                            >
                                <span>🖨️</span>
                                <span>Print Payslip</span>
                            </button>
                            <button 
                                onClick={handlePrintPayslip} 
                                className="flex-1 sm:flex-initial bg-emerald-600 hover:bg-emerald-700 text-white px-3.5 py-1.5 rounded-lg font-bold text-xs shadow-xs transition-colors cursor-pointer flex items-center justify-center gap-1.5"
                                title="Download the compressed 1-page PDF document"
                            >
                                <span>📄</span>
                                <span>Export PDF (1-Page)</span>
                            </button>
                        </div>
                    </div>

                    {/* Executive KPI Summary Ribbon */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 p-3 bg-slate-50/80 rounded-xl border border-slate-200 text-xs">
                        <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-slate-200/80 shadow-2xs">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold block">🏠 Private Tutoring</span>
                            <span className="text-sm sm:text-base font-black text-blue-700 block mt-0.5">
                                NT${Math.round(tutoringPay).toLocaleString()}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {tutoringClasses.length} sessions ({tutoringHours.toFixed(1)} hrs)
                            </span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-slate-200/80 shadow-2xs">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold block">🏫 Cram School(s)</span>
                            <span className="text-sm sm:text-base font-black text-purple-700 block mt-0.5">
                                NT${Math.round(cramPay).toLocaleString()}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {cramClasses.length} sessions ({cramHours.toFixed(1)} hrs)
                            </span>
                        </div>
                        <div className="p-2 bg-white dark:bg-gray-800 rounded-lg border border-slate-200/80 shadow-2xs">
                            <span className="text-[11px] text-gray-500 dark:text-gray-400 font-semibold block">🧾 Reimbursements</span>
                            <span className="text-sm sm:text-base font-black text-amber-700 block mt-0.5">
                                NT${Math.round(expensesPay).toLocaleString()}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {expenses.length} approved claims
                            </span>
                        </div>
                        <div className="p-2 bg-emerald-50/90 rounded-lg border border-emerald-200 shadow-2xs">
                            <span className="text-[11px] text-emerald-800 font-bold block">💰 Total Payable (實領)</span>
                            <span className="text-sm sm:text-base font-black text-emerald-700 block mt-0.5">
                                NT${Math.round(grandTotal).toLocaleString()}
                            </span>
                            <span className="text-[10px] text-emerald-600 font-medium">
                                {(tutoringHours + cramHours).toFixed(1)} total teaching hrs
                            </span>
                        </div>
                    </div>

                    {/* ========================================================= */}
                    {/* DIVISION 1: PRIVATE TUTORING                              */}
                    {/* ========================================================= */}
                    <div className="border border-blue-200 rounded-xl overflow-hidden page-break-avoid">
                        <div className="bg-blue-50/90 px-3.5 py-2 border-b border-blue-200 flex flex-wrap justify-between items-center gap-1.5">
                            <div className="flex items-center gap-2">
                                <span className="text-sm">🏠</span>
                                <h4 className="font-extrabold text-xs sm:text-sm text-blue-950 uppercase tracking-wide">
                                    Division 1: Private Tutoring (私人家教課程)
                                </h4>
                                <span className="bg-blue-200 text-blue-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    {tutoringClasses.length} Sessions
                                </span>
                            </div>
                            <div className="text-xs font-bold text-blue-900">
                                Subtotal: NT${Math.round(tutoringPay).toLocaleString()} ({tutoringHours.toFixed(1)} hrs)
                            </div>
                        </div>

                        {tutoringClasses.length === 0 ? (
                            <div className="p-4 text-center text-xs text-gray-400 dark:text-gray-500 italic bg-white dark:bg-gray-800">
                                No private tutoring classes recorded in this pay period.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                    <thead className="bg-gray-50/80 text-gray-600 dark:text-gray-400 font-semibold uppercase text-[11px]">
                                        <tr>
                                            <th className="py-2 px-3 text-left w-24">Date</th>
                                            <th className="py-2 px-3 text-left">Class Name</th>
                                            <th className="py-2 px-3 text-right w-24">Hours</th>
                                            <th className="py-2 px-3 text-right w-24">Rate/Hr</th>
                                            <th className="py-2 px-3 text-right w-28">Session Pay</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                        {tutoringClasses.map((item, index) => (
                                            <tr key={`tutoring-${index}`} className="hover:bg-blue-50/40 transition-colors">
                                                <td className="py-1.5 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                    {item.attendance_date ? new Date(item.attendance_date).toISOString().slice(0, 10) : ''}
                                                </td>
                                                <td className="py-1.5 px-3 font-semibold text-gray-900 dark:text-gray-100">
                                                    {item.class_name}
                                                </td>
                                                <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">
                                                    {parseFloat(item.class_duration_hours).toFixed(2)} hrs
                                                </td>
                                                <td className="py-1.5 px-3 text-right text-gray-600 dark:text-gray-400">
                                                    NT${parseFloat(item.hourly_rate).toFixed(0)}
                                                </td>
                                                <td className="py-1.5 px-3 text-right font-bold text-gray-900 dark:text-gray-100">
                                                    NT${parseFloat(item.session_pay).toFixed(0)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot className="bg-blue-50/50 font-bold text-blue-950 border-t border-blue-200 text-xs">
                                        <tr>
                                            <td colSpan="2" className="py-2 px-3 text-left font-bold">
                                                Tutoring Subtotal ({tutoringClasses.length} sessions):
                                            </td>
                                            <td className="py-2 px-3 text-right">{tutoringHours.toFixed(2)} hrs</td>
                                            <td className="py-2 px-3 text-right text-gray-400 dark:text-gray-500">—</td>
                                            <td className="py-2 px-3 text-right font-extrabold text-blue-900">
                                                NT${Math.round(tutoringPay).toLocaleString()}
                                            </td>
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* ========================================================= */}
                    {/* DIVISION 2: CRAM SCHOOL(S)                                */}
                    {/* ========================================================= */}
                    <div className="border border-purple-200 rounded-xl overflow-hidden page-break-avoid">
                        <div className="bg-purple-50/90 px-3.5 py-2 border-b border-purple-200 flex flex-wrap justify-between items-center gap-1.5">
                            <div className="flex items-center gap-2">
                                <span className="text-sm">🏫</span>
                                <h4 className="font-extrabold text-xs sm:text-sm text-purple-950 uppercase tracking-wide">
                                    Division 2: Cram School(s) (補習班合作課程)
                                </h4>
                                <span className="bg-purple-200 text-purple-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                    {cramClasses.length} Sessions
                                </span>
                            </div>
                            <div className="text-xs font-bold text-purple-900">
                                Subtotal: NT${Math.round(cramPay).toLocaleString()} ({cramHours.toFixed(1)} hrs)
                            </div>
                        </div>

                        {cramClasses.length === 0 ? (
                            <div className="p-4 text-center text-xs text-gray-400 dark:text-gray-500 italic bg-white dark:bg-gray-800">
                                No cram school classes recorded in this pay period.
                            </div>
                        ) : (
                            <div className="divide-y divide-purple-100 dark:divide-gray-700">
                                {cramBySchool.map(school => {
                                    const isExpanded = expandedSchools.has(school.schoolName);
                                    return (
                                        <div key={school.schoolName}>
                                            <button
                                                type="button"
                                                onClick={() => toggleSchoolExpanded(school.schoolName)}
                                                className="w-full flex items-center justify-between gap-2 px-3.5 py-2 bg-purple-50/40 hover:bg-purple-50/80 transition-colors text-left cursor-pointer"
                                            >
                                                <span className="flex items-center gap-2 text-xs font-bold text-purple-950">
                                                    <span className="text-[10px] text-purple-500">{isExpanded ? '▼' : '▶'}</span>
                                                    🏫 {school.schoolName}
                                                    <span className="font-normal text-gray-500 dark:text-gray-400">
                                                        ({school.sessions.length} sessions, {school.hours.toFixed(1)} hrs)
                                                    </span>
                                                </span>
                                                <span className="text-xs font-bold text-purple-900">
                                                    NT${Math.round(school.pay).toLocaleString()}
                                                </span>
                                            </button>

                                            {isExpanded && (
                                                <div className="overflow-x-auto">
                                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                                        <thead className="bg-gray-50/80 text-gray-600 dark:text-gray-400 font-semibold uppercase text-[11px]">
                                                            <tr>
                                                                <th className="py-2 px-3 text-left w-24">Date</th>
                                                                <th className="py-2 px-3 text-left">Class Name</th>
                                                                <th className="py-2 px-3 text-right w-24">Hours</th>
                                                                <th className="py-2 px-3 text-right w-24">Rate/Hr</th>
                                                                <th className="py-2 px-3 text-right w-28">Session Pay</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                                            {school.sessions.map((item, index) => (
                                                                <tr key={`cram-${school.schoolName}-${index}`} className="hover:bg-purple-50/40 transition-colors">
                                                                    <td className="py-1.5 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                                        {item.attendance_date ? new Date(item.attendance_date).toISOString().slice(0, 10) : ''}
                                                                    </td>
                                                                    <td className="py-1.5 px-3 font-semibold text-gray-900 dark:text-gray-100">
                                                                        {item.class_name}
                                                                    </td>
                                                                    <td className="py-1.5 px-3 text-right text-gray-700 dark:text-gray-300">
                                                                        {parseFloat(item.class_duration_hours).toFixed(2)} hrs
                                                                    </td>
                                                                    <td className="py-1.5 px-3 text-right text-gray-600 dark:text-gray-400">
                                                                        NT${parseFloat(item.hourly_rate).toFixed(0)}
                                                                    </td>
                                                                    <td className="py-1.5 px-3 text-right font-bold text-gray-900 dark:text-gray-100">
                                                                        NT${parseFloat(item.session_pay).toFixed(0)}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <div className="bg-purple-50/50 font-bold text-purple-950 border-t border-purple-200 text-xs flex justify-between items-center px-3 py-2">
                                    <span>Cram Schools Subtotal ({cramClasses.length} sessions, {cramHours.toFixed(2)} hrs, {cramBySchool.length} location{cramBySchool.length === 1 ? '' : 's'}):</span>
                                    <span className="font-extrabold text-purple-900">NT${Math.round(cramPay).toLocaleString()}</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ========================================================= */}
                    {/* DIVISION 3: APPROVED EXPENSES (IF ANY)                    */}
                    {/* ========================================================= */}
                    {expenses && expenses.length > 0 && (
                        <div className="border border-amber-200 rounded-xl overflow-hidden page-break-avoid">
                            <div className="bg-amber-50/90 px-3.5 py-2 border-b border-amber-200 flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm">🧾</span>
                                    <h4 className="font-extrabold text-xs sm:text-sm text-amber-950 uppercase tracking-wide">
                                        Division 3: Approved Expense Reimbursements (費用核銷)
                                    </h4>
                                    <span className="bg-amber-200 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
                                        {expenses.length} Items
                                    </span>
                                </div>
                                <div className="text-xs font-bold text-amber-900">
                                    Subtotal: NT${Math.round(expensesPay).toLocaleString()}
                                </div>
                            </div>
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                                <thead className="bg-gray-50/80 text-gray-600 dark:text-gray-400 font-semibold uppercase text-[11px]">
                                    <tr>
                                        <th className="py-2 px-3 text-left w-24">Date</th>
                                        <th className="py-2 px-3 text-left">Description</th>
                                        <th className="py-2 px-3 text-right w-28">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                                    {expenses.map((expense, index) => (
                                        <tr key={`expense-${index}`} className="hover:bg-amber-50/30 transition-colors">
                                            <td className="py-1.5 px-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                                                {expense.submitted_at ? new Date(expense.submitted_at).toISOString().slice(0, 10) : ''}
                                            </td>
                                            <td className="py-1.5 px-3 font-medium text-gray-800 dark:text-gray-200">{expense.description}</td>
                                            <td className="py-1.5 px-3 text-right font-bold text-amber-900">
                                                NT${parseFloat(expense.amount).toFixed(0)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot className="bg-amber-50/50 font-bold text-amber-950 border-t border-amber-200 text-xs">
                                    <tr>
                                        <td colSpan="2" className="py-2 px-3 text-left font-bold">
                                            Expense Reimbursement Total:
                                        </td>
                                        <td className="py-2 px-3 text-right font-extrabold text-amber-900">
                                            NT${Math.round(expensesPay).toLocaleString()}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}

                    {/* ========================================================= */}
                    {/* GRAND TOTAL BAR & SIGN-OFF (PRINTABLE)                    */}
                    {/* ========================================================= */}
                    <div className="bg-slate-900 text-white p-3.5 sm:p-4 rounded-xl flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 page-break-avoid">
                        <div>
                            <span className="text-[11px] text-slate-400 uppercase tracking-wider font-semibold block">
                                Statement Grand Total
                            </span>
                            <span className="text-xs text-slate-300 mt-0.5 block">
                                Tutoring: NT${Math.round(tutoringPay).toLocaleString()} • Cram Schools: NT${Math.round(cramPay).toLocaleString()}
                                {expensesPay > 0 ? ` • Expenses: NT$${Math.round(expensesPay).toLocaleString()}` : ''}
                            </span>
                        </div>
                        <div className="text-left sm:text-right">
                            <span className="text-xs text-emerald-400 font-medium block">Grand Total Payable (實領總金額)</span>
                            <span className="text-2xl sm:text-3xl font-black text-emerald-400 tracking-tight block">
                                NT${Math.round(grandTotal).toLocaleString()}
                            </span>
                        </div>
                    </div>

                    {/* Sign-off Signature Section (Clean for physical archiving/printing) */}
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-between items-center text-xs text-gray-500 dark:text-gray-400 font-medium">
                        <div>Teacher Signature: ___________________________</div>
                        <div>Admin / Finance Verification: ___________________________</div>
                    </div>

                </div>
            )}
            </>
            )}
        </div>
    );
}